import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const POPOVER_WIDTH = 320;
const VIEWPORT_MARGIN = 12;

const DEFAULT_TITLE = 'Save and lock translation?';
const DEFAULT_MESSAGE =
  'This will save the current translation and lock it so other users cannot edit it until it is unlocked.';

function computePosition(anchorEl, popoverEl) {
  if (!anchorEl) {
    return { top: VIEWPORT_MARGIN, left: VIEWPORT_MARGIN };
  }

  const anchorRect = anchorEl.getBoundingClientRect();
  const popoverHeight = popoverEl?.offsetHeight || 168;
  const popoverWidth = popoverEl?.offsetWidth || POPOVER_WIDTH;

  let left = anchorRect.right - popoverWidth;
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - popoverWidth - VIEWPORT_MARGIN),
  );

  let top = anchorRect.bottom + 8;
  if (top + popoverHeight > window.innerHeight - VIEWPORT_MARGIN) {
    top = anchorRect.top - popoverHeight - 8;
  }
  top = Math.max(VIEWPORT_MARGIN, top);

  return { top, left };
}

function SaveAndLockConfirmPopover({
  open,
  anchorRef,
  onClose,
  onConfirm,
  confirming = false,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  confirmLabel = 'Save & Lock',
  cancelLabel = 'Cancel',
  dialogLabel = 'Confirm save and lock',
}) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchorEl = anchorRef?.current;
    const popoverEl = popoverRef.current;
    setPosition(computePosition(anchorEl, popoverEl));
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    requestAnimationFrame(() => updatePosition());

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    const onPointerDown = (event) => {
      const popoverEl = popoverRef.current;
      const anchorEl = anchorRef?.current;
      if (popoverEl?.contains(event.target) || anchorEl?.contains(event.target)) {
        return;
      }
      onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, onClose, anchorRef, updatePosition]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="save-lock-confirm-popover"
      role="dialog"
      aria-modal="true"
      aria-label={dialogLabel}
      style={{ top: position.top, left: position.left }}
    >
      <h3 className="save-lock-confirm-title">{title}</h3>
      <p className="save-lock-confirm-message">{message}</p>
      <div className="save-lock-confirm-actions">
        <button
          type="button"
          className="btn btn-secondary btn-small save-lock-confirm-cancel"
          onClick={onClose}
          disabled={confirming}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className="btn btn-success btn-small save-lock-confirm-submit"
          onClick={onConfirm}
          disabled={confirming}
        >
          {confirming ? (
            <>
              <span className="spinner" aria-hidden="true" />
              Saving...
            </>
          ) : (
            confirmLabel
          )}
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default SaveAndLockConfirmPopover;
